import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, Alert, CircularProgress, InputAdornment,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Navbar from '../components/Navbar.js';
import { ENDPOINTS } from '../utils/api.js';

/**
 * Admin password reset — landing page for the emailed link (/reset-password/:uid/:token).
 * Validates the token on mount, then lets the admin set a new password.
 */
export default function ResetPasswordPage() {
  const { uid, token } = useParams();
  const navigate = useNavigate();

  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${ENDPOINTS.RESET_PASSWORD}?uid=${encodeURIComponent(uid)}&token=${encodeURIComponent(token)}`);
        if (!active) return;
        if (res.ok) {
          const data = await res.json().catch(() => ({}));
          setValid(Boolean(data.valid));
          setUsername(data.username || '');
        } else {
          // Couldn't pre-check (e.g. rate-limited or offline) — don't claim the link is expired;
          // show the form and let the submit re-validate the token.
          setValid(true);
        }
      } catch {
        if (active) setValid(true); // network hiccup — let the submit be the real gate
      }
      if (active) setChecking(false);
    })();
    return () => { active = false; };
  }, [uid, token]);

  const submit = async () => {
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(ENDPOINTS.RESET_PASSWORD, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, token, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setDone(true);
      } else {
        setError(data.message || 'Could not reset your password. The link may have expired.');
      }
    } catch { setError('Network error. Please try again.'); }
    setSubmitting(false);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Navbar />
      <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, py: { xs: 6, sm: 10 } }}>
        <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, maxWidth: 440, width: '100%', borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
          {checking ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
          ) : done ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'success.main', mb: 1.5 }} />
              <Typography variant="h6" fontWeight={700} gutterBottom>Password set</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                You can now sign in with your username and new password.
              </Typography>
              <Button variant="contained" onClick={() => navigate('/login')} sx={{ borderRadius: 2.5, textTransform: 'none' }}>
                Go to sign in
              </Button>
            </Box>
          ) : !valid ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Link expired or invalid</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                This password-reset link is no longer valid. Request a new one from the sign-in page.
              </Typography>
              <Button variant="outlined" onClick={() => navigate('/login')} sx={{ borderRadius: 2.5, textTransform: 'none' }}>
                Back to sign in
              </Button>
            </Box>
          ) : (
            <>
              <Typography variant="h6" fontWeight={700} gutterBottom>Set a new password</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                {username ? <>For <strong>{username}</strong>. </> : null}
                Use at least 8 characters.
              </Typography>
              {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
              <TextField
                fullWidth type="password" label="New password" value={password}
                onChange={(e) => setPassword(e.target.value)} sx={{ mb: 2 }}
                InputProps={{ startAdornment: (<InputAdornment position="start"><LockOutlinedIcon fontSize="small" color="action" /></InputAdornment>) }}
              />
              <TextField
                fullWidth type="password" label="Confirm password" value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submit()} sx={{ mb: 2.5 }}
                InputProps={{ startAdornment: (<InputAdornment position="start"><LockOutlinedIcon fontSize="small" color="action" /></InputAdornment>) }}
              />
              <Button
                fullWidth variant="contained" onClick={submit}
                disabled={submitting || !password || !confirm}
                sx={{ py: 1.25, borderRadius: 2.5, textTransform: 'none' }}
              >
                {submitting ? <CircularProgress size={20} color="inherit" /> : 'Set password'}
              </Button>
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
